import React, { useEffect, useRef, useState } from "react";
import { Loader2, RotateCcw, Upload, Trash2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCrypto } from "../context/CryptoContext";
import { CERT_DEFAULTS, CertificatePreview, certificateHtml, certData, useLogoSrc } from "./Certificate";

// Optional logo for the round seal. Without one, the seal shows your Beacon's first letter.
const SealLogo = ({ path, onChange }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { window.alert("Please choose an image under 5 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("enc", "false");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } catch { window.alert("Couldn't upload, please try again."); } finally { setBusy(false); }
  };
  return (
    <div>
      <span className="font-body text-[11px] text-zinc-500">Seal logo (optional, a square image works best)</span>
      <div className="flex items-center gap-2 mt-1">
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} className="hidden" data-testid="certificate-logo-input" />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="certificate-logo-upload" className="inline-flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-1.5 font-body text-xs hover:bg-white/5 disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {path ? "Replace logo" : "Upload logo"}
        </button>
        {path && <button type="button" onClick={() => onChange("")} data-testid="certificate-logo-remove" className="inline-flex items-center gap-1 font-body text-xs text-zinc-500 hover:text-red-400"><Trash2 size={12} /> Use the letter instead</button>}
      </div>
    </div>
  );
};

const inp = "w-full bg-[#0A0A0A] border border-white/10 px-2.5 py-1.5 font-body text-sm text-white outline-none focus:border-white/30";
const FIELDS = [
  ["heading", "Heading", 48], ["intro", "Line above the name", 60], ["completed", "Line above the course title", 80],
];
const STYLES = [["script", "Handwritten script"], ["plain", "Simple typed name"], ["none", "No signature"]];

const Field = ({ label, children }) => <label className="block"><span className="font-body text-[11px] text-zinc-500">{label}</span>{children}</label>;

// Teacher editor for certificate wording, an optional personal note and signature style, with a live preview.
export const CertificateEditor = ({ beacon, course, onSaved }) => {
  const [c, setC] = useState({ ...CERT_DEFAULTS, signer_name: beacon.creator_name || "", ...(beacon.certificate || {}) });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const logoSrc = useLogoSrc(c.logo);
  const { user } = useAuth();
  const { decryptNetText } = useCrypto() || {};
  useEffect(() => {
    const ef = user?.enc_fields?.name;
    if (beacon.certificate?.signer_name || beacon.creator_name || !ef?.ct || !decryptNetText) return;
    decryptNetText(ef.ct, ef.iv).then((n) => n && setC((p) => (p.signer_name ? p : { ...p, signer_name: n }))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const savedLogo = beacon.certificate?.logo || "";
  const setLogo = (p) => {
    if (c.logo && c.logo !== savedLogo) api.post("/beacon/mine/release-upload", { path: c.logo }).catch(() => {});
    set("logo", p);
  };
  const set = (k, v) => { setC({ ...c, [k]: v }); setSaved(false); };
  const save = async () => {
    setBusy(true);
    try { await api.put("/beacon/mine", { certificate: c }); await onSaved(); setSaved(true); } catch { window.alert("Couldn't save, please try again."); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-3" data-testid="certificate-editor">
      <div className="grid sm:grid-cols-3 gap-2.5">
        {FIELDS.map(([k, label, max]) => <Field key={k} label={label}><input value={c[k]} maxLength={max} onChange={(e) => set(k, e.target.value)} placeholder={CERT_DEFAULTS[k]} data-testid={`certificate-field-${k}`} className={inp} /></Field>)}
      </div>
      <Field label="Personal note (optional, shown under the course title)">
        <textarea value={c.note} maxLength={220} rows={2} onChange={(e) => set("note", e.target.value)} placeholder="e.g. Thank you for learning with me. I can't wait to see what you create next." data-testid="certificate-field-note" className={`${inp} resize-none`} />
      </Field>
      <div>
        <span className="font-body text-[11px] text-zinc-500">Signature</span>
        <div className="flex flex-wrap gap-2 mt-1">
          {STYLES.map(([k, label]) => (
            <button key={k} type="button" onClick={() => set("signature_style", k)} data-testid={`certificate-sig-${k}`}
              className={`px-3 py-1.5 border font-body text-xs transition-colors ${c.signature_style === k ? "border-[#14A89A] text-white bg-[#14A89A]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}>{label}</button>
          ))}
        </div>
      </div>
      {c.signature_style !== "none" && <div className="grid sm:grid-cols-2 gap-2.5">
        <Field label="Name to sign with (your profile name is private, so it only appears here once you save)"><input value={c.signer_name} maxLength={60} onChange={(e) => set("signer_name", e.target.value)} placeholder="Your name" data-testid="certificate-field-signer_name" className={inp} /></Field>
        <Field label="Title under the signature"><input value={c.signer_title} maxLength={30} onChange={(e) => set("signer_title", e.target.value)} placeholder="Teacher" data-testid="certificate-field-signer_title" className={inp} /></Field>
      </div>}
      <SealLogo path={c.logo} onChange={setLogo} />
      <CertificatePreview html={certificateHtml(certData({ ...beacon, certificate: c }, course, "Student Name", logoSrc))} />
      <div className="flex items-center gap-3">
        <button type="button" onClick={save} disabled={busy} data-testid="certificate-save" className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#14A89A] text-[#0A0A0A] font-body text-xs font-semibold disabled:opacity-50">
          {busy && <Loader2 size={12} className="animate-spin" />} Save certificate
        </button>
        <button type="button" onClick={() => { setC({ ...CERT_DEFAULTS, signer_name: c.signer_name || beacon.creator_name || "", logo: c.logo || "" }); setSaved(false); }} data-testid="certificate-reset" className="inline-flex items-center gap-1 font-body text-xs text-zinc-500 hover:text-white">
          <RotateCcw size={11} /> Original wording
        </button>
        {saved && <span className="font-body text-xs text-[#14A89A]" data-testid="certificate-saved">Saved</span>}
      </div>
    </div>
  );
};
